import { METRICS_TABS, MetricsTabId } from './constants';

export type MetricsNotificationSeverity = 'error' | 'warning' | 'info';

export interface MetricsNotification {
  id: string;
  tabId: MetricsTabId;
  title: string;
  message: string;
  severity: MetricsNotificationSeverity;
  timestamp: number;
}

export const NOTIFICATION_SEVERITY: Record<
  MetricsNotificationSeverity,
  { label: string; color: string }
> = {
  error: { label: 'Error', color: '#E85D4C' },
  warning: { label: 'Warning', color: '#E8A317' },
  info: { label: 'Info', color: '#4C9AE8' },
};

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const now = Date.now();

const minutesAgo = (minutes: number) => now - minutes * MINUTE;
const hoursAgo = (hours: number) => now - hours * HOUR;

/** Placeholder feed until notification payloads have a real shape. */
const mockMetricsNotifications: MetricsNotification[] = [
  {
    id: 'sm-error-rate',
    tabId: 'sagemaker',
    title: 'Endpoint error rate climbed',
    message:
      'error-counter reached 18 in the latest 5-minute window on the scoring endpoint.',
    severity: 'error',
    timestamp: minutesAgo(6),
  },
  {
    id: 'sm-retries',
    tabId: 'sagemaker',
    title: 'Retries above baseline',
    message:
      'retries-counter is 17, higher than the usual range for this endpoint.',
    severity: 'warning',
    timestamp: minutesAgo(18),
  },
  {
    id: 'sm-started',
    tabId: 'sagemaker',
    title: 'Scoring run started',
    message: 'started-counter increased to 28 over the last interval.',
    severity: 'info',
    timestamp: minutesAgo(27),
  },
  {
    id: 'sm-success',
    tabId: 'sagemaker',
    title: 'Successes dipped',
    message:
      'successes-counter fell to 11 while started-counter stayed elevated.',
    severity: 'warning',
    timestamp: minutesAgo(41),
  },
  {
    id: 'sm-latency',
    tabId: 'sagemaker',
    title: 'Invocation latency rose',
    message:
      'Model endpoint p95 latency is above the last hour for the scoring endpoint.',
    severity: 'warning',
    timestamp: minutesAgo(52),
  },
  {
    id: 'sm-throttle',
    tabId: 'sagemaker',
    title: 'Invocations throttled',
    message: 'A burst of requests was throttled before they reached the model.',
    severity: 'error',
    timestamp: hoursAgo(1),
  },
  {
    id: 'sm-cpu',
    tabId: 'sagemaker',
    title: 'Instance CPU saturated',
    message:
      'CPU on the endpoint instance stayed near capacity for 10 minutes.',
    severity: 'error',
    timestamp: hoursAgo(2),
  },
  {
    id: 'sm-memory',
    tabId: 'sagemaker',
    title: 'Memory pressure',
    message:
      'Endpoint memory usage crossed the warning line and has not fallen.',
    severity: 'warning',
    timestamp: minutesAgo(100),
  },
  {
    id: 'sm-scale-out',
    tabId: 'sagemaker',
    title: 'Endpoint scaled out',
    message: 'Instance count increased after the invocation backlog grew.',
    severity: 'info',
    timestamp: hoursAgo(3),
  },
  {
    id: 'sm-health-check',
    tabId: 'sagemaker',
    title: 'Health check failed',
    message: 'One endpoint instance failed its health check and was replaced.',
    severity: 'error',
    timestamp: hoursAgo(4),
  },
  {
    id: 'sm-cold-start',
    tabId: 'sagemaker',
    title: 'Cold start detected',
    message:
      'A new instance served its first requests slower than the baseline.',
    severity: 'info',
    timestamp: hoursAgo(5),
  },
  {
    id: 'sm-disk',
    tabId: 'sagemaker',
    title: 'Disk space low',
    message: 'Model artifact storage on the instance is nearly full.',
    severity: 'warning',
    timestamp: hoursAgo(6),
  },
  {
    id: 'sm-timeouts',
    tabId: 'sagemaker',
    title: 'Invocation timeouts',
    message:
      'Several scoring calls timed out before the model returned a result.',
    severity: 'error',
    timestamp: hoursAgo(8),
  },
  {
    id: 'sm-recovered',
    tabId: 'sagemaker',
    title: 'Error rate recovered',
    message:
      'error-counter dropped back toward the normal range after the spike.',
    severity: 'info',
    timestamp: hoursAgo(10),
  },
  {
    id: 'sm-deploy',
    tabId: 'sagemaker',
    title: 'Model variant deployed',
    message: 'A new model variant started receiving traffic on the endpoint.',
    severity: 'info',
    timestamp: hoursAgo(14),
  },
  {
    id: 'queue-depth',
    tabId: 'queue',
    title: 'Queue depth growing',
    message:
      'Messages are waiting longer than they did over the previous hour.',
    severity: 'error',
    timestamp: minutesAgo(12),
  },
  {
    id: 'queue-lag',
    tabId: 'queue',
    title: 'Consumer lag detected',
    message: 'Workers are retrying failed jobs faster than they are clearing.',
    severity: 'warning',
    timestamp: minutesAgo(54),
  },
  {
    id: 'scoring-errors',
    tabId: 'scoring',
    title: 'Instance errors spiked',
    message:
      'error-counter jumped on the primary scoring instance in the last window.',
    severity: 'error',
    timestamp: minutesAgo(9),
  },
  {
    id: 'scoring-slow',
    tabId: 'scoring',
    title: 'Slow scoring calls',
    message: 'slow-counter is elevated across the last 30 minutes.',
    severity: 'warning',
    timestamp: minutesAgo(33),
  },
  {
    id: 'scoring-dropped',
    tabId: 'scoring',
    title: 'Dropped calls recovered',
    message:
      'dropped-counter returned to the normal range after a short spike.',
    severity: 'info',
    timestamp: hoursAgo(2),
  },
  {
    id: 'db-pool',
    tabId: 'database',
    title: 'Connection wait time rose',
    message: 'Queries are spending longer waiting on the connection pool.',
    severity: 'warning',
    timestamp: minutesAgo(22),
  },
  {
    id: 'sentry-typeerror',
    tabId: 'sentry',
    title: 'New exception group',
    message: 'TypeError captured in the scoring callback handler.',
    severity: 'error',
    timestamp: minutesAgo(4),
  },
  {
    id: 'sentry-volume',
    tabId: 'sentry',
    title: 'Error volume up',
    message: 'Event rate is about 3× the 24-hour baseline.',
    severity: 'error',
    timestamp: minutesAgo(15),
  },
  {
    id: 'sentry-rejection',
    tabId: 'sentry',
    title: 'Unhandled rejection',
    message: 'A promise rejection was captured on the API process.',
    severity: 'warning',
    timestamp: minutesAgo(48),
  },
  {
    id: 'sentry-resolved',
    tabId: 'sentry',
    title: 'Issue marked quiet',
    message: 'The timeout group from yesterday has not fired again today.',
    severity: 'info',
    timestamp: hoursAgo(5),
  },
  {
    id: 'sentry-release',
    tabId: 'sentry',
    title: 'Release regression',
    message: 'Errors started shortly after the latest deploy.',
    severity: 'warning',
    timestamp: hoursAgo(3),
  },
];

mockMetricsNotifications.sort(
  (left, right) => right.timestamp - left.timestamp
);

export const MOCK_METRICS_NOTIFICATIONS = mockMetricsNotifications;

export const notificationsForTab = (tabId: MetricsTabId) =>
  MOCK_METRICS_NOTIFICATIONS.filter((item) => item.tabId === tabId);

export const notificationCountForTab = (tabId: MetricsTabId) =>
  notificationsForTab(tabId).length;

export const tabLabel = (tabId: MetricsTabId) =>
  METRICS_TABS.find((tab) => tab.id === tabId)?.label || tabId;
